import { get, set, del, createStore, getMany, keys, delMany } from 'idb-keyval';

const NS = 'nudge';

export const idb = {
  async getDeviceId(): Promise<string | null> {
    return (await get(`${NS}:device_id`)) ?? null;
  },
  async setDeviceId(id: string): Promise<void> {
    await set(`${NS}:device_id`, id);
  },
  async getToken(): Promise<string | null> {
    return (await get(`${NS}:token`)) ?? null;
  },
  async setToken(t: string | null): Promise<void> {
    if (t === null) await del(`${NS}:token`);
    else await set(`${NS}:token`, t);
  },
  async getCircleId(): Promise<string | null> {
    return (await get(`${NS}:circle_id`)) ?? null;
  },
  async setCircleId(id: string | null): Promise<void> {
    if (id === null) await del(`${NS}:circle_id`);
    else await set(`${NS}:circle_id`, id);
  },
  async getFlag(key: string): Promise<unknown> {
    return await get(`${NS}:flag:${key}`);
  },
  async setFlag(key: string, value: unknown): Promise<void> {
    await set(`${NS}:flag:${key}`, value);
  },
  async getPendingInvite(): Promise<string | null> {
    return (await get(`${NS}:pending_invite_code`)) ?? null;
  },
  async setPendingInvite(code: string): Promise<void> {
    await set(`${NS}:pending_invite_code`, code);
  },
  async clearPendingInvite(): Promise<void> {
    await del(`${NS}:pending_invite_code`);
  },
  async clear(): Promise<void> {
    await Promise.all([
      del(`${NS}:device_id`),
      del(`${NS}:token`),
      del(`${NS}:circle_id`),
    ]);
  },
};

// Offline queue lives in a separate IndexedDB store
export interface QueueEntry {
  id: string;
  action: 'send' | 'clear' | 'ack';
  payload: unknown;
  url: string;
  method: string;
  timestamp: number;
}

const queueStore = createStore('nudge-offline', 'queue');

export const offlineQueue = {
  async add(entry: QueueEntry): Promise<void> {
    await set(entry.id, entry, queueStore);
  },
  async list(): Promise<QueueEntry[]> {
    const ks = await keys(queueStore);
    if (ks.length === 0) return [];
    const vals = await getMany<QueueEntry>(ks as IDBValidKey[], queueStore);
    return vals.filter((v): v is QueueEntry => !!v);
  },
  async remove(id: string): Promise<void> {
    await del(id, queueStore);
  },
  async clear(): Promise<void> {
    const ks = await keys(queueStore);
    if (ks.length) await delMany(ks as IDBValidKey[], queueStore);
  },
};

export function ensureDeviceId(): Promise<string> {
  return idb.getDeviceId().then(async (existing) => {
    if (existing) return existing;
    const fresh = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
      ? crypto.randomUUID()
      : `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    await idb.setDeviceId(fresh);
    return fresh;
  });
}
